import { Box, Grid, Typography } from "@material-ui/core";
import React, { Fragment, useEffect, useState } from "react";
import { UseFormReturn, useFormState, useWatch } from "react-hook-form";
import ControlledCheckbox from "../../../app/components/inputs/ControlledCheckbox";
import ControlledInput from "../../../app/components/inputs/ControlledInput";
import ControlledSelect, { SelectOption } from "../../../app/components/inputs/ControlledSelect";
import ServiceStatusLabel from "../../cases/components/ServiceStatusLabel";
import { ServiceStatusEnum } from "../../cases/enums/ServiceStatusEnum";
import { getLocationOptions, LocationDto } from "../../locations/dtos/LocationDto";
import { ProgramDto } from "../../programs/dtos/ProgramDto";
import * as Yup from "yup";
import { StaffMemberDto } from "../../staff-members/dtos/StaffMemberDto";
import ControlledWholeNumberInput from "../../../app/components/inputs/ControlledWholeNumberInput";
import { CaseFormDto } from "../../cases/dtos/CaseFormDto";
import { JjisProgramTypeEnum } from "../../jjis/enums/JjisProgramTypeEnum";
import { ScreeningDto } from "../../screenings/dtos/ScreeningDto";
import { calculateDateDifferenceInDays, programIsType, screeningIsDvRespite } from "../../../app/helpers";
import ScreeningSelect from "../../screenings/components/ScreeningSelect";
import ProgramControlledSelect from "../../programs/components/ProgramControlledSelect";
import InfoTooltip from "../../../app/components/InfoTooltip";
import { IntakeSchoolStatusEnum, intakeSchoolStatusOptions } from "../enums/IntakeSchoolStatusEnum";
import { IntakeGradeLevelEnum, intakeGradeLevelOptions } from "../enums/IntakeGradeLevelEnum";
import ControlledAutocomplete from "../../../app/components/inputs/ControlledAutocomplete";
import { ProgramFundingSourceEnum } from "../../programs/enums/ProgramFundingSourceEnum";
import { DemographicsFamilyStructureEnum, DemographicsFamilyStructureEnumOptions } from "../../demographics/enums/DemographicsFamilyStructureEnum";
import {StaffMemberControlledAutoComplete} from "../../staff-members/components/StaffMemberControlledAutoComplete";
import {MultiStaffMemberControlledSelector} from "../../staff-members/components/MultiStaffMemberControlledSelector";
import CaseCourtOrderAttachmentUploadComponent from "../../cases/components/CaseCourtOrderAttachmentUploadComponent";

export const lastGradeAttendedStatus: string[] = [
  IntakeSchoolStatusEnum.DroppedOut,
  IntakeSchoolStatusEnum.Expelled,
  IntakeSchoolStatusEnum.DoNotKnow,
  IntakeSchoolStatusEnum.NotEnrolled,
];

export const currentGradeStatus: string[] = [
  IntakeSchoolStatusEnum.AttendingIrregularly,
  IntakeSchoolStatusEnum.AttendingRegularly,
  IntakeSchoolStatusEnum.Suspended,
  IntakeSchoolStatusEnum.NotInSession,
];

export const intakeAtSchema = Yup.date().label("Intake Date/Time").typeError("Invalid Date/Time format.").required("Intake Date is required.");

export const intakeSchema = Yup.object().shape({
  intakeAt: intakeAtSchema
    .test("validate-not-equal-to-dob", "The Intake Date cannot be the same or prior to the Youth's Date of Birth.", function (value) {
      if (value && this.parent.youthDob) {
        const intakeAt = new Date(value);
        intakeAt.setHours(0, 0, 0, 0);
        const youthDob = new Date(this.parent.youthDob);
        youthDob.setHours(0, 0, 0, 0);
        if (intakeAt <= youthDob) {
          return false;
        }
      }
      return true;
    })
    .test(
      "validate-screening-date",
      // eslint-disable-next-line no-template-curly-in-string
      "${label} can be no more than 60 days after the screening date/time.",
      function (value) {
        if (this.parent.screeningAt == null || this.parent.screeningAt === "") return true;

        if (value) {
          const intakeAt = new Date(value);
          const screeningAt = new Date(this.parent.screeningAt);
          const dateDiff = calculateDateDifferenceInDays(intakeAt, screeningAt);

          if (dateDiff >= 60) return false;
        }

        return true;
      }
    ),
  serviceStatus: Yup.string()
    .label("Service Status")
    .required()
    .test("valid-service-status", "Invalid Service Status", function (value) {
      return Object.keys(ServiceStatusEnum).includes(value || "");
    }),
  locationId: Yup.string().label("Location").required(),
  programId: Yup.string().label("Program").required(),
  youthMedicationCount: Yup.string().when("isYouthOnMedication", {
    is: true,
    then: Yup.string()
      .label("Number of Medications")
      .required()
      .test("valid-youth-medication", "Number must be greater than 0 if Youth is on medication.", (value) => {
        if (value === undefined || value === "") return true;

        return !isNaN(+value) && +value > 0;
      }),
  }),
  intakeStaffId: Yup.string().label("Staff Completing Intake").required(),
  schoolStatus: Yup.string().label("School Status").required(),
  lastGradeAttended: Yup.string()
    .label("Last Grade Attended")
    .when("schoolStatus", {
      is: (value: string) => lastGradeAttendedStatus.includes(value || ""),
      then: Yup.string().required(),
    }),
  currentGrade: Yup.string()
    .label("Current Grade")
    .when("schoolStatus", {
      is: (value: string) => currentGradeStatus.includes(value || ""),
      then: Yup.string().required(),
    }),
  schoolName: Yup.string()
    .label("School Name")
    .when("schoolStatus", {
      is: (value: string) => currentGradeStatus.includes(value || ""),
      then: Yup.string().required().nullable().max(200),
    }),
  familyStructure: Yup.string()
    .label("Family Structure")
    .when("fundingSource", {
      is: ProgramFundingSourceEnum.FloridaNetwork,
      then: (schema) => schema.required(),
    }),
  courtOrderDocumentFilename: Yup.string()
    .label("Court Order Document")
    .when("isCourtOrdered", {
      is: true,
      then: (schema) => schema.required("Documentation is required for Court Ordered cases."),
    }),   
});

interface Props {
  form: UseFormReturn<CaseFormDto>;
  disabled: boolean;
  programs: ProgramDto[];
  locations: LocationDto[];
  staffMembers: StaffMemberDto[];
  screenings: ScreeningDto[];
  screeningsAreLoading: boolean;
  programsAreLoading: boolean;
  locationsAreLoading: boolean;
  staffMembersAreLoading: boolean;
  schoolNames: string[];
  schoolNamesAreLoading: boolean;
  isLegacy: boolean
  onStaffMemberAdded: (id: string) => void;
  onStaffMemberRemoved: (id: string) => void;
  onCourtOrderDocumentAttached: (filename: string) => void;
}

const serviceStatusOptions: SelectOption[] = [
  { value: "", label: "Select Service Status" },
  {
    value: ServiceStatusEnum.FullAdmission,
    label: <ServiceStatusLabel status={ServiceStatusEnum.FullAdmission} />,
  },
  {
    value: ServiceStatusEnum.OrientationProbation,
    label: <ServiceStatusLabel status={ServiceStatusEnum.OrientationProbation} />,
  },
  {
    value: ServiceStatusEnum.DropIn,
    label: <ServiceStatusLabel status={ServiceStatusEnum.DropIn} />,
  },
  {
    value: ServiceStatusEnum.Hotline,
    label: <ServiceStatusLabel status={ServiceStatusEnum.Hotline} />,
  },
  {
    value: ServiceStatusEnum.StreetOutreach,
    label: <ServiceStatusLabel status={ServiceStatusEnum.StreetOutreach} />,
  },
];

const IntakeFormFields: React.FC<Props> = ({
  form,
  disabled,
  programs,
  locations,
  staffMembers,
  screenings,
  schoolNames,
  screeningsAreLoading,
  staffMembersAreLoading,
  programsAreLoading,
  locationsAreLoading,
  schoolNamesAreLoading,
  isLegacy,
  onStaffMemberAdded,
  onStaffMemberRemoved,
  onCourtOrderDocumentAttached
}) => {
  const { control } = form;

  const { isYouthOnMedication, programId, startFromScreening, screeningId, intakeAt, schoolStatus, fundingSource, isCourtOrdered } = useWatch({
    control,
  });
  const [selectionPrograms, setSelectionPrograms] = useState<ProgramDto[]>(programs);
  const [selectionLocations, setSelectionLocations] = useState<LocationDto[]>(locations);

  useEffect(() => {
    setSelectionLocations(locations);
  }, [locations]);

  useEffect(() => {
    if (startFromScreening && !screeningIsDvRespite(screeningId!, screenings)) {
      setSelectionPrograms(programs.filter((x) => x.programType.jjisProgramType !== JjisProgramTypeEnum.DVRespite));
    } else {
      setSelectionPrograms(programs);
    }
  }, [programs, startFromScreening, screeningId, screenings]);

  const { setValue, trigger } = form;

  const { errors } = useFormState({ control });
  
  const handleFileUpdated = (filename: string) => {
    setValue("courtOrderDocumentFilename", filename);
    trigger("courtOrderDocumentFilename");
  }

  return (
    <Fragment>
      <Grid item md={12} xs={12}>
        <ControlledInput
          name="intakeAt"
          label="Intake Date/Time*"
          type="datetime-local"
          control={control}
          disabled={disabled}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item md={12} xs={12}>
        <ScreeningSelect
          name="screeningId"
          control={control}
          disabled={disabled || startFromScreening === true}
          screenings={screenings}
          isLoading={screeningsAreLoading}
          screeningId={screeningId!}
          programIsDvRespite={programIsType(programId!, programs, JjisProgramTypeEnum.DVRespite)}
        />
      </Grid>
      <Grid item md={12} xs={12}>
        <StaffMemberControlledAutoComplete
          control={control}
          disabled={disabled}
          name="intakeStaffId"
          label="Staff Completing Intake*"
          isLoading={staffMembersAreLoading}
          staffMembers={staffMembers}
        />
      </Grid>
      {/* <Grid item md={12} xs={12}>
        <StaffMemberControlledAutoComplete
          control={control}
          disabled={disabled}
          name="caseManagerId"
          label="Case Manager"
          isLoading={staffMembersAreLoading}
          staffMembers={staffMembers}
        />
      </Grid> */}
      <Grid item md={12} xs={12}>
        <MultiStaffMemberControlledSelector
          control={control}
          name="primaryStaffIds"
          staffMembers={staffMembers}
          onStaffAdded={onStaffMemberAdded}
          onStaffRemoved={onStaffMemberRemoved}
        />
      </Grid>
      
      <Grid item md={12} xs={12}>
        <ControlledSelect name="serviceStatus" label="Service Status*" control={control} disabled={disabled} options={serviceStatusOptions} fullWidth />
      </Grid>

      <Grid item md={12} xs={12}>
        <ProgramControlledSelect
          name="programId"
          label="Program*"
          control={control}
          disabled={disabled || selectionPrograms.length === 0}
          programs={selectionPrograms}
          isLoading={programsAreLoading}
          defaultOptionLabel={selectionPrograms.length > 0 ? "Select Program" : "No Programs Active For Intake Date"}
        />
      </Grid>
      <Grid item md={12} xs={12}>
        <ControlledSelect
          name="locationId"
          label="Program Location*"
          control={control}
          disabled={disabled || selectionLocations.length === 0}
          options={getLocationOptions(
            selectionLocations,
            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" padding="0px">
              <div>{!programId ? "Select a Program to See Locations" : selectionLocations.length > 0 ? "Select Location" : "No Locations Active"}</div>{" "}
              {intakeAt && (
                <InfoTooltip
                  title={
                    <Typography>
                      Only currently active Program/Locations that were also active on {new Date(intakeAt).toLocaleDateString()} are displayed.
                    </Typography>
                  }
                />
              )}
            </Box>
          )}
          isLoading={locationsAreLoading}
          fullWidth
        />
      </Grid>

      <Grid item md={12} xs={12}>
        <ControlledSelect control={control} name="schoolStatus" options={intakeSchoolStatusOptions} label="School Status*" fullWidth disabled={disabled} />
      </Grid>

      {lastGradeAttendedStatus.includes(schoolStatus || "") && (
        <Grid item md={12} xs={12}>
          <ControlledSelect
            control={control}
            name="lastGradeAttended"
            options={intakeGradeLevelOptions.filter((o) => isLegacy || o.value !== IntakeGradeLevelEnum.NotSpecified)}
            label="Last Grade Attended*"
            fullWidth
            disabled={disabled}
          />
        </Grid>
      )}

      {currentGradeStatus.includes(schoolStatus || "") && (
        <>
          <Grid item md={12} xs={12}>
            <ControlledSelect
              control={control}
              name="currentGrade"
              options={intakeGradeLevelOptions.filter((o) => isLegacy || o.value !== IntakeGradeLevelEnum.NotSpecified)}
              label="Current Grade*"
              disabled={disabled}
              fullWidth
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <ControlledAutocomplete
              options={schoolNames.sort((a, b) => (a > b ? 1 : -1))}
              freeSolo
              loading={schoolNamesAreLoading}
              autoSelect
              openOnFocus
              name="schoolName"
              label={"School Name*"}
              control={control}
              renderInput={() => <Fragment></Fragment>}
            />
          </Grid>
        </>
      )}
      <Grid item md={12} xs={12}>
        <ControlledSelect
          name="familyStructure"
          label={`Family Structure${fundingSource === ProgramFundingSourceEnum.FloridaNetwork ? "*" : ""}`}
          control={control}
          disabled={disabled}
          options={DemographicsFamilyStructureEnumOptions.filter((o) => o.value !== DemographicsFamilyStructureEnum.NotSpecified) }
          fullWidth
        />
      </Grid>

      <Grid item md={12} xs={12}>
        <ControlledCheckbox name="isCourtOrdered" label="Is this a court ordered case?" control={control} disabled={disabled} color="primary" />
      </Grid>
      
      {isCourtOrdered && 
           <Grid item md={12} xs={12}>
            <CaseCourtOrderAttachmentUploadComponent 
              onCourtOrderedAttachmentUploaded={handleFileUpdated}
              error={errors.courtOrderDocumentFilename && errors.courtOrderDocumentFilename.message} 
              disabled = {disabled} />
          </Grid>         
        }         

      {programIsType(programId!, programs, JjisProgramTypeEnum.Shelter) && (
        <Grid item md={12} xs={12}>
          <ControlledCheckbox name="isYouthOnMedication" label="Is Youth on Medication?" control={control} disabled={disabled} color="primary" />
          {isYouthOnMedication && (
            <ControlledWholeNumberInput name="youthMedicationCount" label="Number of Medications*" control={control} disabled={disabled} fullWidth={false} />
          )}
        </Grid>
      )}
    </Fragment>
  );
};

export default IntakeFormFields;
